/**
 * Periodic version checker.
 *
 * Every CHECK_INTERVAL_MS, fetches index.html (no-cache) and extracts the
 * hashed entry script tag. If it differs from the one currently loaded,
 * we know a new build is live and we reload the page once.
 *
 * Only runs when the tab is visible to avoid background churn.
 */

const CHECK_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
const STORAGE_KEY = "app:reloaded-for-version";

let currentVersion: string | null = null;
let timer: number | null = null;

async function fetchEntryHash(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?_v=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) return null;
    const html = await res.text();
    // Match the main entry script: /assets/index-XXXXXX.js
    const match = html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/);
    return match ? match[0] : null;
  } catch {
    return null;
  }
}

async function checkVersion() {
  if (document.hidden) return;
  const latest = await fetchEntryHash();
  if (!latest) return;

  if (!currentVersion) {
    currentVersion = latest;
    return;
  }

  if (latest !== currentVersion) {
    // Avoid reload loops: only reload once per detected version.
    const lastReloaded = sessionStorage.getItem(STORAGE_KEY);
    if (lastReloaded === latest) return;
    sessionStorage.setItem(STORAGE_KEY, latest);

    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString());
    window.location.replace(url.toString());
  }
}

export function startVersionCheck() {
  if (timer !== null) return;

  // Capture baseline shortly after load.
  void fetchEntryHash().then((v) => {
    currentVersion = v;
  });

  timer = window.setInterval(checkVersion, CHECK_INTERVAL_MS);

  // Check immediately when the tab regains focus.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void checkVersion();
  });
}
